import { useRef, useSyncExternalStore } from "react";
import { TypedUseSelectorHook, useDispatch, useStore } from "react-redux";
import { AppDispatch, RootState } from "./store";

export const useAppDispatch = () => useDispatch<AppDispatch>();

type EqualityFn = (left: unknown, right: unknown) => boolean;

const defaultEqual: EqualityFn = (left, right) => left === right;

/**
 * react-redux 8.1.3 enveloppe le sélecteur dans
 * `useCallback({ [selector.name]() {} }[selector.name], deps)`.
 * Avec un sélecteur anonyme (`name === ""`), le rendu suivant plante dans
 * `areHookInputsEqual` (`Cannot read properties of undefined (reading 'length')`).
 * On lit le store directement et on mémorise le résultat par état.
 */
function useAppSelectorImpl(
  selector: (state: RootState) => unknown,
  equalityFnOrOptions?: EqualityFn | { equalityFn?: EqualityFn },
) {
  const equalityFn =
    typeof equalityFnOrOptions === "function"
      ? equalityFnOrOptions
      : equalityFnOrOptions?.equalityFn ?? defaultEqual;
  const store = useStore<RootState>();
  const latest = useRef({
    selector,
    equalityFn,
    hasValue: false,
    state: undefined as unknown,
    value: undefined as unknown,
  });
  latest.current.selector = selector;
  latest.current.equalityFn = equalityFn;

  const getSnapshot = () => {
    const state = store.getState();
    const box = latest.current;
    if (box.hasValue && box.state === state) {
      return box.value;
    }
    const next = box.selector(state);
    if (box.hasValue && box.equalityFn(box.value, next)) {
      box.state = state;
      return box.value;
    }
    box.hasValue = true;
    box.state = state;
    box.value = next;
    return next;
  };

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

export const useAppSelector =
  useAppSelectorImpl as TypedUseSelectorHook<RootState>;
